/**
 * Relatórios > Financeiro > Receitas.
 *
 * Vendas do período (mensal ou anual): cards de resumo (MoM/YoY, canais
 * Site/Vendedor/PO, primeira compra, recorrente e margem bruta), gráfico de
 * barras diárias/mensais com métricas togglable, produtos agrupados por
 * categoria, top produtos/categorias (valor/quantidade) e vendas por UF.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronRight,
  Download,
  MapPin,
  Package,
  TrendingUp,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { clsx } from 'clsx';
import financialReportsService from '@services/financialReportsService';
import type { RevenueReport, RevenueStateRow } from '@/types/financialReports';
import { formatCurrencyBRL } from '@pages/financial/financialUtils';
import {
  ErrorBox,
  LoadingSpinner,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
  selectCls,
} from '@pages/financial/financialShared';
import {
  ChartTooltip,
  CompanySelect,
  DeltaBadge,
  SERIES_COLORS,
  getMonthOptions,
  getYearOptions,
  kFormatter,
  useCompanies,
} from './financialReportsShared';
import { useUserPreferences, PREF_FIN_REPORT_REVENUE } from '@hooks/useUserPreferences';
import { useIsMobile } from '@hooks/useIsMobile';
import {
  formatPct,
  pctDelta,
  pctOf,
  revenueAnnualChartRows,
  revenueDailyChartRows,
  topCategories,
  topProductsFromCategories,
  verticalBarChartHeight,
} from './financialReportsUtils';

// O mapa carrega sob demanda: ele arrasta o ECharts (~640 KB) e o contorno das
// UFs, que só interessam a quem abre este relatório. Sem o lazy, esse peso
// entraria no bundle principal de TODAS as telas do ERP.
const RevenueGeoSection = React.lazy(() => import('./RevenueGeoSection'));

type RevenueMode = 'mensal' | 'anual';

interface RevenuePrefs extends Record<string, unknown> {
  mode?: RevenueMode;
  company_id?: number;
}

// ─── Cards de resumo ────────────────────────────────────────────

const SummaryDeltaCard: React.FC<{
  label: string;
  value: string;
  pct?: number | null;
  badge?: React.ReactNode;
  valueClassName?: string;
}> = ({ label, value, pct, badge, valueClassName }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
    <p className="text-sm text-slate-500">{label}</p>
    <div className="flex items-center gap-2 mt-1 flex-wrap">
      <p className={`text-xl font-bold ${valueClassName ?? 'text-slate-900'}`}>{value}</p>
      {pct !== undefined && <DeltaBadge pct={pct} />}
      {badge}
    </div>
  </div>
);

/** Texto "% do total" dos cards Primeira Compra / Recorrente. */
const shareOfTotalText = (part: number, total: number): string => {
  const share = pctOf(part, total);
  return share === null ? '—' : `${formatPct(share)} do total`;
};

// ─── Métricas do gráfico diário (chips togglable) ───────────────

const DAILY_METRICS = [
  { key: 'total', label: 'Total' },
  { key: 'site', label: 'Site' },
  { key: 'seller', label: 'Vendedor' },
  { key: 'po', label: 'PO' },
  { key: 'firstPurchase', label: 'Primeira Compra' },
  { key: 'recurring', label: 'Recorrente' },
  { key: 'earn', label: 'Margem Bruta' },
] as const;

type DailyMetricKey = (typeof DAILY_METRICS)[number]['key'];

/** Cor fixa por métrica (posição na lista → SERIES_COLORS). */
const metricColor = (key: DailyMetricKey): string =>
  SERIES_COLORS[DAILY_METRICS.findIndex(m => m.key === key) % SERIES_COLORS.length];

const DEFAULT_METRICS: DailyMetricKey[] = ['total', 'site', 'earn'];

// ─── Tooltip do gráfico de vendas por UF (valor + qtd) ──────────

const StateBarTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as RevenueStateRow;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      <p className="text-slate-700">Valor: {formatCurrencyBRL(row.value)}</p>
      <p className="text-slate-500">Qtd: {row.qty.toLocaleString('pt-BR')}</p>
    </div>
  );
};

/**
 * Largura do eixo Y de categorias do gráfico de mais vendidos, calculada pelo
 * rótulo mais longo para exibir o nome completo (sem truncar).
 */
const categoryAxisWidth = (labels: string[], isMobile = false): number => {
  const maxLen = labels.reduce((max, l) => Math.max(max, l.length), 0);
  const width = Math.min(340, Math.max(120, Math.round(maxLen * 6.5) + 16));
  // No celular o eixo de 340px não deixaria espaço para as barras.
  return isMobile ? Math.min(140, width) : width;
};

// ─── View principal ─────────────────────────────────────────────

export const RevenueReportView: React.FC = () => {
  const isMobile = useIsMobile();
  const now = new Date();
  const [mode, setMode] = useState<RevenueMode>('mensal');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const { companies, companiesError } = useCompanies();

  const [report, setReport] = useState<RevenueReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [selectedMetrics, setSelectedMetrics] = useState<DailyMetricKey[]>(DEFAULT_METRICS);
  const [showAccumulated, setShowAccumulated] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [topBy, setTopBy] = useState<'value' | 'qty'>('value');
  const [topGroupBy, setTopGroupBy] = useState<'category' | 'product'>('category');

  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<RevenuePrefs>(PREF_FIN_REPORT_REVENUE);
  const [filtersReady, setFiltersReady] = useState(false);

  // Restaura os filtros salvos antes da primeira busca (o período fica no mês corrente)
  useEffect(() => {
    if (!prefsLoaded) return;
    if (prefs?.mode === 'mensal' || prefs?.mode === 'anual') setMode(prefs.mode);
    if (prefs?.company_id != null) setCompanyId(prefs.company_id);
    setFiltersReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  useEffect(() => {
    if (!filtersReady) return;
    savePrefs({ mode, company_id: companyId ?? undefined });
  }, [filtersReady, mode, companyId, savePrefs]);

  const fetchData = useCallback(async () => {
    if (!filtersReady) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await financialReportsService.getRevenueReport(year, mode === 'mensal' ? month : null, companyId));
      setExpandedCategories(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório de receitas');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, mode, companyId, filtersReady]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await financialReportsService.exportRevenueCsv(year, month, companyId);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Erro ao exportar CSV');
    } finally {
      setExporting(false);
    }
  };

  const toggleMetric = (key: DailyMetricKey) =>
    setSelectedMetrics(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

  const toggleCategory = (categoryId: number) =>
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });

  const chartData = report
    ? mode === 'anual'
      ? revenueAnnualChartRows(report.dailySales)
      : revenueDailyChartRows(report.dailySales)
    : [];
  const activeMetrics = DAILY_METRICS.filter(m => selectedMetrics.includes(m.key));
  const topProducts = report
    ? topGroupBy === 'category'
      ? topCategories(report.categories, report.categories.length, topBy)
      : topProductsFromCategories(report.categories, 10, topBy)
    : [];
  const topAxisWidth = categoryAxisWidth(topProducts.map(p => p.description), isMobile);
  const categoriesTotalValue = report ? report.categories.reduce((s, c) => s + c.value, 0) : 0;
  const categoriesTotalQty = report ? report.categories.reduce((s, c) => s + c.qty, 0) : 0;
  const stateTotal = report ? report.byState.reduce((s, r) => s + r.value, 0) : 0;

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Período</label>
            <div className="inline-flex w-full sm:w-auto rounded-lg border border-slate-300 overflow-hidden">
              {(['mensal', 'anual'] as RevenueMode[]).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={clsx(
                    'flex-1 sm:flex-none px-4 py-2 text-sm font-medium transition-colors',
                    mode === m ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {m === 'mensal' ? 'Mensal' : 'Anual'}
                </button>
              ))}
            </div>
          </div>
          {mode === 'mensal' && (
            <div className="w-full sm:w-auto">
              <label className={labelCls}>Mês</label>
              <select className={`${selectCls} w-full sm:w-32`} value={month} onChange={e => setMonth(Number(e.target.value))}>
                {getMonthOptions().map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Ano</label>
            <select className={`${selectCls} w-full sm:w-28`} value={year} onChange={e => setYear(Number(e.target.value))}>
              {getYearOptions().map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Empresa</label>
            <CompanySelect companies={companies} value={companyId} onChange={setCompanyId} />
          </div>
          <button onClick={fetchData} disabled={loading} className={`${primaryBtnCls} w-full sm:w-auto justify-center`}>
            <Calendar size={16} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
          {mode === 'mensal' && (
            <button onClick={handleExport} disabled={exporting || loading} className={secondaryBtnCls}>
              <Download size={16} />
              {exporting ? 'Exportando...' : 'Exportar CSV'}
            </button>
          )}
        </div>
      </div>

      {companiesError && <ErrorBox message={companiesError} />}
      {exportError && <ErrorBox message={exportError} onRetry={handleExport} />}
      {error && <ErrorBox message={error} onRetry={fetchData} />}
      {loading && <LoadingSpinner />}

      {!loading && !error && report && (
        <>
          {/* Cards de resumo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <SummaryDeltaCard
              label={mode === 'mensal' ? 'Total do Mês' : 'Total do Ano'}
              value={formatCurrencyBRL(report.summary.monthTotal)}
              valueClassName="text-emerald-700"
            />
            <SummaryDeltaCard
              label={mode === 'mensal' ? 'Mês Anterior' : 'Ano Anterior'}
              value={formatCurrencyBRL(report.summary.prevMonthTotal)}
              pct={pctDelta(report.summary.monthTotal, report.summary.prevMonthTotal)}
            />
            {mode === 'mensal' && (
              <SummaryDeltaCard
                label="Mesmo Mês Ano Anterior"
                value={formatCurrencyBRL(report.summary.samePeriodLastYearTotal)}
                pct={pctDelta(report.summary.monthTotal, report.summary.samePeriodLastYearTotal)}
              />
            )}
            <SummaryDeltaCard
              label="Site"
              value={formatCurrencyBRL(report.summary.siteTotal)}
              badge={
                <span className="text-xs font-medium text-slate-500">
                  {shareOfTotalText(report.summary.siteTotal, report.summary.monthTotal)}
                </span>
              }
            />
            <SummaryDeltaCard
              label="Vendedor"
              value={formatCurrencyBRL(report.summary.sellerTotal)}
              badge={
                <span className="text-xs font-medium text-slate-500">
                  {shareOfTotalText(report.summary.sellerTotal, report.summary.monthTotal)}
                </span>
              }
            />
            <SummaryDeltaCard
              label="PO"
              value={formatCurrencyBRL(report.summary.poTotal)}
              badge={
                <span className="text-xs font-medium text-slate-500">
                  {shareOfTotalText(report.summary.poTotal, report.summary.monthTotal)}
                </span>
              }
            />
            <SummaryDeltaCard
              label="Primeira Compra"
              value={formatCurrencyBRL(report.summary.firstPurchaseTotal)}
              badge={
                <span className="text-xs font-medium text-slate-500">
                  {shareOfTotalText(report.summary.firstPurchaseTotal, report.summary.monthTotal)}
                </span>
              }
            />
            <SummaryDeltaCard
              label="Compra Recorrente"
              value={formatCurrencyBRL(report.summary.recurringTotal)}
              badge={
                <span className="text-xs font-medium text-slate-500">
                  {shareOfTotalText(report.summary.recurringTotal, report.summary.monthTotal)}
                </span>
              }
            />
            <SummaryDeltaCard
              label="Margem Bruta"
              value={formatCurrencyBRL(report.summary.grossMargin)}
              valueClassName="text-emerald-700"
              badge={
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                  {formatPct(report.summary.grossMarginPct)} da venda total
                </span>
              }
            />
          </div>

          {/* Gráfico de vendas diárias (métricas togglable) */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <TrendingUp size={18} className="text-emerald-600" />
              <h3 className="text-base font-semibold text-slate-800">
                {mode === 'mensal' ? 'Vendas Diárias' : 'Vendas Mensais'}
              </h3>
            </div>
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">Nenhuma venda encontrada no período.</p>
            ) : (
              <>
                {/* Multi-select de métricas (chips) + toggle de acumulado */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {DAILY_METRICS.map(metric => {
                    const selected = selectedMetrics.includes(metric.key);
                    const color = metricColor(metric.key);
                    return (
                      <button
                        key={metric.key}
                        type="button"
                        onClick={() => toggleMetric(metric.key)}
                        className={clsx(
                          'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                          selected ? 'text-white' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50',
                        )}
                        style={selected ? { backgroundColor: color, borderColor: color } : undefined}
                      >
                        {metric.label}
                      </button>
                    );
                  })}
                  <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden />
                  <button
                    type="button"
                    onClick={() => setShowAccumulated(prev => !prev)}
                    className={clsx(
                      'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                      showAccumulated
                        ? 'bg-slate-700 border-slate-700 text-white'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50',
                    )}
                  >
                    Acumulado
                  </button>
                </div>

                {activeMetrics.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">
                    Selecione ao menos uma métrica para exibir o gráfico.
                  </p>
                ) : (
                  <div className="h-64 sm:h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#64748b' }} interval={isMobile ? 4 : 'preserveEnd'} />
                        <YAxis
                          yAxisId="daily"
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          tickFormatter={kFormatter}
                        />
                        {showAccumulated && (
                          <YAxis
                            yAxisId="acc"
                            orientation="right"
                            tick={{ fontSize: 12, fill: '#94a3b8' }}
                            tickFormatter={kFormatter}
                          />
                        )}
                        <Tooltip content={<ChartTooltip />} />
                        {!isMobile && <Legend />}
                        {activeMetrics.map(metric => (
                          <Bar
                            key={metric.key}
                            yAxisId="daily"
                            dataKey={metric.key}
                            name={metric.label}
                            fill={metricColor(metric.key)}
                            radius={[3, 3, 0, 0]}
                          />
                        ))}
                        {showAccumulated &&
                          activeMetrics.map(metric => (
                            <Line
                              key={`${metric.key}Acc`}
                              yAxisId="acc"
                              type="monotone"
                              dataKey={`${metric.key}Acc`}
                              name={`${metric.label} (acum.)`}
                              stroke={metricColor(metric.key)}
                              strokeWidth={2}
                              strokeDasharray="6 3"
                              dot={false}
                            />
                          ))}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Produtos por categoria (tabela expansível) */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Package size={18} className="text-emerald-600" />
              <h3 className="text-base font-semibold text-slate-800">Vendas por Categoria</h3>
            </div>
            {report.categories.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nenhum produto vendido no período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Categoria / Produto</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">% do Total</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Qtd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.categories.map(cat => {
                      const expanded = expandedCategories.has(cat.categoryId);
                      const share = pctOf(cat.value, categoriesTotalValue);
                      return (
                        <React.Fragment key={cat.categoryId}>
                          <tr className="border-b border-slate-100 bg-slate-50/50 hover:bg-slate-50">
                            <td className="py-2 px-3">
                              <button
                                type="button"
                                onClick={() => toggleCategory(cat.categoryId)}
                                className="inline-flex items-center gap-2 font-semibold text-slate-800 hover:text-emerald-700"
                              >
                                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                {cat.description}
                              </button>
                            </td>
                            <td className="py-2 px-3 text-right font-semibold text-slate-900">{formatCurrencyBRL(cat.value)}</td>
                            <td className="py-2 px-3 text-right text-slate-600">{share === null ? '—' : formatPct(share)}</td>
                            <td className="py-2 px-3 text-right text-slate-700">{cat.qty.toLocaleString('pt-BR')}</td>
                          </tr>
                          {expanded &&
                            (cat.products.length === 0 ? (
                              <tr className="border-b border-slate-100">
                                <td colSpan={4} className="py-2 px-3 pl-4 sm:pl-10 text-slate-400 italic">
                                  Nenhum produto na categoria.
                                </td>
                              </tr>
                            ) : (
                              cat.products.map(p => (
                                <tr key={p.sku} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="py-1.5 px-3 pl-4 sm:pl-10 text-slate-700">
                                    <span className="font-mono text-xs text-slate-400 mr-2">{p.sku}</span>
                                    {p.description}
                                  </td>
                                  <td className="py-1.5 px-3 text-right text-slate-700">{formatCurrencyBRL(p.value)}</td>
                                  <td className="py-1.5 px-3" />
                                  <td className="py-1.5 px-3 text-right text-slate-500">{p.qty.toLocaleString('pt-BR')}</td>
                                </tr>
                              ))
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold">
                      <td className="py-2 px-3 text-slate-700">Total</td>
                      <td className="py-2 px-3 text-right text-slate-900">{formatCurrencyBRL(categoriesTotalValue)}</td>
                      <td className="py-2 px-3 text-right text-slate-500">100%</td>
                      <td className="py-2 px-3 text-right text-slate-900">{categoriesTotalQty.toLocaleString('pt-BR')}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Produtos mais vendidos (top 10) */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <BarChart3 size={18} className="text-emerald-600" />
                <h3 className="text-base font-semibold text-slate-800">
                  {topGroupBy === 'category' ? 'Vendas por Categoria' : 'Produtos Mais Vendidos'}
                </h3>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs font-medium">
                  <button
                    type="button"
                    onClick={() => setTopGroupBy('category')}
                    className={clsx(
                      'px-3 py-1.5 transition-colors',
                      topGroupBy === 'category'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50',
                    )}
                  >
                    Categoria
                  </button>
                  <button
                    type="button"
                    onClick={() => setTopGroupBy('product')}
                    className={clsx(
                      'px-3 py-1.5 border-l border-slate-300 transition-colors',
                      topGroupBy === 'product'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50',
                    )}
                  >
                    Produto
                  </button>
                </div>
              <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setTopBy('value')}
                  className={clsx(
                    'px-3 py-1.5 transition-colors',
                    topBy === 'value' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  Valor
                </button>
                <button
                  type="button"
                  onClick={() => setTopBy('qty')}
                  className={clsx(
                    'px-3 py-1.5 border-l border-slate-300 transition-colors',
                    topBy === 'qty' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  Quantidade
                </button>
              </div>
              </div>
            </div>
            {topProducts.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nenhum produto vendido no período.</p>
            ) : (
              <div style={{ height: verticalBarChartHeight(topProducts.length) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topProducts} layout="vertical" margin={{ left: 8, right: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={topBy === 'value' ? kFormatter : v => Number(v).toLocaleString('pt-BR')}
                    />
                    <YAxis
                      type="category"
                      dataKey="description"
                      width={topAxisWidth}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                    />
                    <Tooltip
                      content={
                        <ChartTooltip
                          valueFormatter={topBy === 'value' ? undefined : (v: number) => v.toLocaleString('pt-BR')}
                        />
                      }
                    />
                    <Bar
                      dataKey={topBy}
                      name={topBy === 'value' ? 'Valor' : 'Quantidade'}
                      fill={topBy === 'value' ? SERIES_COLORS[0] : SERIES_COLORS[1]}
                      radius={[0, 4, 4, 0]}
                      barSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Mapa do Brasil + municípios (drill por estado) */}
          <React.Suspense
            fallback={
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
                <LoadingSpinner />
              </div>
            }
          >
            <RevenueGeoSection
              states={report.byState}
              topCities={report.topCities ?? []}
              year={year}
              month={mode === 'mensal' ? month : null}
            />
          </React.Suspense>

          {/* Vendas por estado */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <MapPin size={18} className="text-emerald-600" />
              <h3 className="text-base font-semibold text-slate-800">Vendas por Estado</h3>
            </div>
            {report.byState.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Nenhuma venda por estado no período.</p>
            ) : (
              <>
                <div className="h-56 sm:h-64 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.byState}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="state" tick={{ fontSize: isMobile ? 10 : 12, fill: '#64748b' }} interval={isMobile ? 1 : 0} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={kFormatter} />
                      <Tooltip content={<StateBarTooltip />} />
                      <Bar dataKey="value" name="Valor" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">UF</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Valor</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">Qtd</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-600">% do Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byState.map(s => {
                        const share = pctOf(s.value, stateTotal);
                        return (
                          <tr key={s.state} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-3 font-medium text-slate-700">{s.state}</td>
                            <td className="py-2 px-3 text-right text-slate-900">{formatCurrencyBRL(s.value)}</td>
                            <td className="py-2 px-3 text-right text-slate-700">{s.qty.toLocaleString('pt-BR')}</td>
                            <td className="py-2 px-3 text-right text-slate-500">{share === null ? '—' : formatPct(share)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50 font-semibold">
                        <td className="py-2 px-3 text-slate-700">Total</td>
                        <td className="py-2 px-3 text-right text-slate-900">{formatCurrencyBRL(stateTotal)}</td>
                        <td className="py-2 px-3 text-right text-slate-900">
                          {report.byState.reduce((s, r) => s + r.qty, 0).toLocaleString('pt-BR')}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-500">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RevenueReportView;
